import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  dismissAiImpactGuidance,
  isAiImpactGuidanceDismissed,
} from "./aiImpactGuidanceStorage";

export type AIImpactAssessmentGuidanceMessageProps = Readonly<{
  open: boolean;
  onClose: () => void;
  /** Manual-initiated flow copy points users to Assessment Center for manual history. */
  variant?: "default" | "manual";
  /**
   * `anchored` — renders via Popper portal; does not affect surrounding layout.
   * Requires `anchorEl`. `inline` — normal document flow.
   */
  placement?: "anchored" | "inline";
  /** Anchor element for `placement="anchored"` (e.g. Assessment Center button). */
  anchorEl?: HTMLElement | null;
}>;

function GuidanceTooltipContent({
  body,
  dontShowAgain,
  onDontShowAgainChange,
  onGotIt,
}: Readonly<{
  body: string;
  dontShowAgain: boolean;
  onDontShowAgainChange: (checked: boolean) => void;
  onGotIt: () => void;
}>) {
  return (
    <Box
      sx={{
        width: "min(288px, calc(100vw - 24px))",
        pt: "10px",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: 3,
          left: "50%",
          transform: "translateX(-50%) rotate(45deg)",
          width: 16,
          height: 16,
          bgcolor: "#fff",
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          p: "21px",
          borderRadius: "12px",
          backgroundColor: "#fff",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0px -5px 50px -6px rgba(0, 0, 0, 0.25)",
        }}
      >
        <Box sx={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 14, color: "primary.main" }} />
          </Box>
          <Typography
            sx={{
              flex: 1,
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "22.75px",
              color: "#111929",
            }}
          >
            {body}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Button
            variant="contained"
            fullWidth
            onClick={onGotIt}
            sx={{
              textTransform: "none",
              py: "8px",
              borderRadius: "8px",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              fontSize: "14px",
              fontWeight: 700,
              lineHeight: "20px",
              boxShadow: (theme) =>
                `0px 10px 15px -3px ${alpha(theme.palette.primary.main, 0.16)}, 0px 4px 6px -4px ${alpha(theme.palette.primary.main, 0.12)}`,
              "&:hover": {
                bgcolor: "primary.dark",
                boxShadow: "none",
              },
            }}
          >
            Got it
          </Button>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                color="primary"
                checked={dontShowAgain}
                onChange={(e) => onDontShowAgainChange(e.target.checked)}
                sx={{
                  p: 0,
                  mr: "8px",
                  color: "#94a3b8",
                  "& .MuiSvgIcon-root": { fontSize: 14, borderRadius: "4px" },
                  "&.Mui-checked": { color: "primary.main" },
                }}
              />
            }
            label={
              <Typography
                sx={{ fontSize: "11px", lineHeight: "16.5px", color: "#94a3b8" }}
              >
                Don&apos;t show again
              </Typography>
            }
            sx={{ m: 0, alignItems: "center" }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default function AIImpactAssessmentGuidanceMessage({
  open,
  onClose,
  variant = "default",
  placement = "inline",
  anchorEl = null,
}: AIImpactAssessmentGuidanceMessageProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const isVisible = open && !isAiImpactGuidanceDismissed();
  const isAnchored = placement === "anchored";

  const handleClose = () => {
    setDontShowAgain(false);
    onClose();
  };

  const handleGotIt = () => {
    if (dontShowAgain) {
      dismissAiImpactGuidance();
    }
    handleClose();
  };

  const body =
    variant === "manual"
      ? "You can find the history and results of manual assessment here."
      : "You can find the summary and history of Auto Impact Assessment results here.";

  if (!isVisible) return null;
  if (isAnchored && !anchorEl) return null;

  const content = (
    <GuidanceTooltipContent
      body={body}
      dontShowAgain={dontShowAgain}
      onDontShowAgainChange={setDontShowAgain}
      onGotIt={handleGotIt}
    />
  );

  if (isAnchored) {
    return (
      <Popper
        open
        anchorEl={anchorEl}
        placement="bottom"
        sx={{ zIndex: 1300 }}
        modifiers={[
          { name: "offset", options: { offset: [0, 10] } },
          { name: "preventOverflow", options: { padding: 8 } },
        ]}
      >
        {content}
      </Popper>
    );
  }

  return content;
}
